/**
 * The four sections of `/rezerwacje` and how the chosen one rides in the address.
 *
 * **Kalendarz · Grafik · Ustawienia · Rozliczenia**, as the A1 canon draws the strip. Only the
 * calendar is built; the other three say where the module lives today. The keys are Polish because
 * the app's addresses are (`/konto?tab=kolor-marki`), and the default serialises to nothing, so
 * `/rezerwacje` and `/rezerwacje?tab=kalendarz` are not two addresses for one screen — the rule and
 * its reason are `pages/account/model/accountTabs`'s.
 */

export const BOOKING_TABS = ['kalendarz', 'grafik', 'ustawienia', 'rozliczenia'] as const;

export type BookingTab = (typeof BOOKING_TABS)[number];

export const DEFAULT_BOOKING_TAB: BookingTab = 'kalendarz';

export interface BookingSearch {
  readonly tab?: BookingTab;
  /**
   * The client whose ledger Rozliczenia opens on — how the client card's „Otwórz rozliczenie"
   * lands on its own client. Meaningful on that tab alone, and dropped everywhere else.
   */
  readonly klient?: number;
}

function isBookingTab(value: unknown): value is BookingTab {
  return typeof value === 'string' && (BOOKING_TABS as readonly string[]).includes(value);
}

/** The route's `validateSearch`: an unknown tab and the default are both dropped from the address. */
export function validateBookingSearch(raw: Record<string, unknown>): BookingSearch {
  const tab = isBookingTab(raw.tab) ? raw.tab : undefined;
  if (tab === undefined || tab === DEFAULT_BOOKING_TAB) return {};
  const klient = Number(raw.klient);
  return tab === 'rozliczenia' && Number.isInteger(klient) && klient > 0
    ? { tab, klient }
    : { tab };
}

/** The client an address asks Rozliczenia to open on, or `null`. */
export function bookingClientOf(search: BookingSearch): number | null {
  return search.tab === 'rozliczenia' && search.klient !== undefined ? search.klient : null;
}

/** The address of one client's ledger on Rozliczenia. */
export function ledgerClientSearch(customerId: number): BookingSearch {
  return { tab: 'rozliczenia', klient: customerId };
}

/** Which tab an address is asking for. Anything unrecognised is the calendar. */
export function bookingTabOf(search: BookingSearch): BookingTab {
  return isBookingTab(search.tab) ? search.tab : DEFAULT_BOOKING_TAB;
}

/** The address a tab should be at. */
export function bookingTabSearch(tab: BookingTab): BookingSearch {
  return tab === DEFAULT_BOOKING_TAB ? {} : { tab };
}
